import {inject, Injectable} from '@angular/core';
import {MarkdownContent, MarkdownContentService} from './markdown-content.service';
import {TranslateHelperService} from './translate-helper.service';

export interface BlogArticle {
	id: string;
	title: string;
	description: string;
	slug: string;
	date: Date | string;
	category: string;
	readTime: number;
	imageUrl?: string;
	keywords?: string;
}

@Injectable({
	           providedIn: 'root'
           })
export class BlogService {
	private readonly markdownService = inject(MarkdownContentService);
	private readonly translateService = inject(TranslateHelperService);
	
	// List of blog article slugs
	private readonly blogSlugs = [
		'test-bdsm', 'roles-bdsm', 'rack-ssc', 'bdsm-contract', 'saint-valentin-bdsm',
		'debuter-bdsm', 'sub-drop-dom-drop', 'bdsm-distance', 'bdsm-communaute'
	];

	async getAllArticles(): Promise<BlogArticle[]> {
		const currentLang = this.translateService.currentLang || 'en';
		const results = await Promise.all(this.blogSlugs.map(async (slug) => {
			try {
				const content = await this.markdownService.loadBlogContent(slug, currentLang);
				return this.createArticleFromMetadata(slug, content.metadata);
			} catch {
				return null;
			}
		}));

		return results
			.filter((article): article is BlogArticle => article !== null)
			.sort((a, b) => {
				const dateA = this.getDateTimestamp(a.date);
				const dateB = this.getDateTimestamp(b.date);
				return dateB - dateA;
			});
	}

	async getArticleBySlug(slug: string): Promise<BlogArticle | undefined> {
		const currentLang = this.translateService.currentLang || 'en';
		try {
			const content = await this.markdownService.loadBlogContent(slug, currentLang);
			return this.createArticleFromMetadata(slug, content.metadata);
		} catch {
			return undefined;
		}
	}

	async getArticlesByCategory(category: string): Promise<BlogArticle[]> {
		const articles = await this.getAllArticles();
		return articles.filter(article => article.category === category);
	}

	async getCategories(): Promise<string[]> {
		const articles = await this.getAllArticles();
		return [...new Set(articles.map(article => article.category))];
	}

	private createArticleFromMetadata(slug: string, metadata?: MarkdownContent['metadata']): BlogArticle {
		return {
			id: slug,
			slug: slug,
			title: metadata?.title || slug,
			description: metadata?.description || '',
			category: metadata?.category || '',
			date: this.normalizeDate(metadata?.date),
			readTime: metadata?.readTime || 0,
			imageUrl: metadata?.imageUrl,
			keywords: metadata?.keywords
		};
	}

	private normalizeDate(value?: string): Date | string {
		if (!value) {
			return '';
		}
		if (/^\d{4}-\d{2}-\d{2}$/u.test(value)) {
			const [year, month, day] = value.split('-').map(Number);
			return new Date(year, month - 1, day);
		}
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? value : parsed;
	}

	private getDateTimestamp(value: BlogArticle['date']): number {
		if (!value) {
			return 0;
		}
		if (value instanceof Date) {
			return value.getTime();
		}
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
	}
}

